import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ModalService } from 'npx-family-happy-common';
import { Subject, takeUntil } from 'rxjs';
import { B2BCompanyExtended } from '../b2b-company-services/b2b-company.services';
import { B2BCompanyServices } from '../b2b-company-services/b2b-company.services';
import { AlertComponent } from 'src/app/shared/components/modals/alert/alert.component';
import { FirebaseStorageService } from 'src/app/shared/services/firebase-storage.service';
import { B2BCompanyCatalogComponent } from '../b2b-company-catalog/b2b-company-catalog.component';
import { CatalogSummary } from '../b2b-company-catalog/catalog.model';

const SIZES = [
  { value: 'EXTRA_SMALL', label: '1-10 dipendenti' },
  { value: 'SMALL', label: '11-50 dipendenti' },
  { value: 'MEDIUM', label: '51-200 dipendenti' },
  { value: 'LARGE', label: '200+ dipendenti' },
];

/** Creazione azienda (solo anagrafica) e dettaglio con servizi, prodotti e prezzi collegati. */
@Component({
  selector: 'app-b2b-company-detail',
  templateUrl: './b2b-company-detail.component.html',
  styleUrls: ['./b2b-company-detail.component.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, AlertComponent, B2BCompanyCatalogComponent],
})
export class B2BCompanyDetailComponent implements OnInit, OnDestroy {
  readonly sizes = SIZES;
  tab: 'servizi' | 'anagrafica' = 'servizi';
  /** Conteggi dell'intestazione, arrivano dal catalogo dopo il caricamento. */
  summary: CatalogSummary | null = null;

  companyForm = new UntypedFormGroup({
    id: new UntypedFormControl('', [Validators.required]),
    name: new UntypedFormControl('', [Validators.required]),
    vatNumber: new UntypedFormControl(''),
    size: new UntypedFormControl('', [Validators.required]),
    logoUrl: new UntypedFormControl(''),
    passive: new UntypedFormControl(false),
  });

  company: B2BCompanyExtended | null = null;
  isEditMode = false;
  showDeleteConfirm: boolean = false;
  formInvalid = false;
  selectedFile: File | null = null;
  uploadProgress = 0;
  private unsubscribe = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private b2bCompanyServices: B2BCompanyServices,
    private modalService: ModalService,
    private firebaseStorage: FirebaseStorageService
  ) {}

  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'create') {
      this.isEditMode = true;
      this.loadCompany(id);
    } else {
      this.isEditMode = false;
    }
  }

  get initials(): string {
    return (this.company?.name ?? '')
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0])
      .join('')
      .toUpperCase();
  }

  /** Il modello comune tipizza `size` come enum numerico, ma il backend restituisce la stringa (es. "LARGE"). */
  sizeLabel(size: unknown): string {
    return SIZES.find((s) => s.value === String(size))?.label ?? 'Dimensione n.d.';
  }

  private loadCompany(id: string) {
    this.b2bCompanyServices
      .getCompanyById(id)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (company) => {
          this.company = company;
          this.companyForm.patchValue({
            id: company.id,
            name: company.name,
            vatNumber: company.vatNumber,
            size: company.size,
            logoUrl: company.logoUrl,
            passive: company.passive,
          });
        },
        error: (error) => {
          this.modalService.showErrorMessage(
            "Errore nel caricamento dell'azienda"
          );
          this.router.navigate(['/b2b-companies']);
        },
      });
  }

  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files[0]) {
      this.selectedFile = input.files[0];
      this.uploadLogo();
    }
  }

  uploadLogo() {
    if (!this.selectedFile || !this.companyForm.get('id')?.value) return;

    const path = this.firebaseStorage.getCompanyLogoPath(
      this.companyForm.get('id')?.value,
      this.selectedFile.name
    );

    this.firebaseStorage
      .uploadFile(this.selectedFile, path)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (downloadUrl) => {
          this.companyForm.patchValue({ logoUrl: downloadUrl });
          this.modalService.showSuccessMessage('Logo caricato con successo');
        },
        error: (error) => {
          this.modalService.showErrorMessage('Errore nel caricamento del logo');
        },
      });
  }

  saveCompany() {
    if (this.companyForm.valid) {
      const formValue = this.companyForm.value;
      if (this.isEditMode) {
        // Update existing company
        this.b2bCompanyServices
          .updateCompany(
            formValue.id,
            formValue.name,
            formValue.vatNumber,
            formValue.size,
            formValue.logoUrl,
            formValue.passive
          )
          .pipe(takeUntil(this.unsubscribe))
          .subscribe({
            next: () => {
              this.modalService.showSuccessMessage(
                'Azienda aggiornata con successo'
              );
              this.loadCompany(this.company!.id);
            },
            error: (error) => {
              this.modalService.showErrorMessage(
                "Errore nell'aggiornamento dell'azienda"
              );
            },
          });
      } else {
        this.b2bCompanyServices
          .createCompany(
            formValue.id,
            formValue.name,
            formValue.vatNumber,
            formValue.size,
            formValue.logoUrl,
            formValue.passive
          )
          .pipe(takeUntil(this.unsubscribe))
          .subscribe(() => {
            this.modalService.showSuccessMessage('Azienda creata con successo');
            this.goBackToCompanies();
          });
      }
    }
  }

  deleteCompany() {
    this.showDeleteConfirm = true;
  }

  confirmDelete(answer: boolean) {
    if (answer && this.company && this.company.id !== null) {
      this.b2bCompanyServices.deleteCompany(this.company.id).subscribe(() => {
        this.modalService.showSuccessMessage('Azienda eliminata');
        this.goBackToCompanies();
      });
    }
    this.showDeleteConfirm = false;
  }

  validatorErrorMessage(fieldName: string) {
    const field = this.companyForm.get(fieldName);
    let message = '';
    if (field?.errors) {
      if (field?.errors['required']) {
        message = `Questo campo è obbligatorio`;
      }
    }
    return message;
  }

  goBackToCompanies() {
    this.router.navigate(['/b2b-companies/']);
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }
}
